import React from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'

// utils
import th from 'utils/themeHelper'
import { updateCommanderToken } from 'utils/localStorageManager'

// assets
const userIcon = 'https://cdn.browniepoints.africa/assets/bpimg_hkk3to4hxef989p.png'

import { Spy } from '@styled-icons/remix-fill/Spy'

// Load components synchronously
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'
import LinkButton from 'components/SmallButtonPrimary/LinkButton'
import Tooltip from 'components/Tooltip'

const Card = styled.div`
  background: white;
  padding: 15px;
  border-radius: 3px;
  border: 1px solid ${th('borders.default')};
`

const Heading = styled.h4`
  margin: 0;
`

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding-top: 1em;
`

const ActionLink = styled(Link)`
  text-decoration: none;
  margin-left: 15px;

  &:first-child {
    margin-left: 0;
  }

  @media screen and (max-width: 769px) {
    margin-bottom: 12px;
  }
`

const Info = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 10px 0;

  @media screen and (max-width: 769px) {
    flex-direction: column;
  }
`

const InfoGroup = styled.div`
  flex: 4;
  margin-top: 20px;

  @media screen and (min-width: 769px) {
    margin: 0;
  }
`

const TeamAndContactInfo = styled.div`
  flex: 1;
`

const TeamLeadInfoWrapper = styled.div`
  color: ${props => props.theme.text.light};
  font-size: 12px;
`

const TeamLeadInfoHeader = styled.div`
  color: ${props => props.theme.secondary.base};
  margin-bottom: 6px;
`

const SchoolNameHeader = styled.div`
  color: ${props => props.theme.secondary.base};
  cursor: pointer;
  margin-bottom: 6px;
  text-decoration: underline;
`

const TeamleadInfoValue = styled.div`
  margin: 0 0 4px;
`

export const AvatarWrapper = styled.div`
  background: ${props => props.theme.backgrounds.dark};
  border: 1px solid ${th('borders.default')};
  border-radius: 50%;
  display: inline-block;
  margin-right: 20px;
  overflow: hidden;
  width: 100px;
  height: 100px;
  position: relative;
`

export const Avatar = styled.img`
  height: 100%;
  width: 100px;
  background: ${props => (props.hasAvatar ? th('backgrounds.dark') : 'white')};
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  object-fit: cover;
  color: white;
  text-align: center;
  font-size: 12px;
  line-height: 100px;
`

const PersonCard = ({ hasGodAdminRole, navigate, person }) => {
  const {
    id,
    f_name,
    l_name,
    team_name,
    school_name,
    email,
    mobile_number,
    avatar_url,
    roleType
  } = person

  const displayableName = f_name ? `${f_name} ${l_name}` : email

  return (
    <Card>
      <Info>
        <AvatarWrapper>
          <Avatar src={avatar_url || userIcon} hasAvatar={avatar_url} alt="member avatar" />
        </AvatarWrapper>

        <InfoGroup>
          <Heading>{displayableName}</Heading>

          <ConditionalDisplay displayWhen={[roleType !== 'Learner']}>
            <Actions>
              <ActionLink to={`/dashboard/manage-person/${id}`}>
                <LinkButton>Edit details</LinkButton>
              </ActionLink>

              <ConditionalDisplay displayWhen={[hasGodAdminRole]}>
                <div style={{ margin: '0 0 0 10px' }}>
                  <LinkButton onClick={() => updateCommanderToken(person.commandeer_token)}>
                    <div style={{ width: '20px', height: '20px' }}>
                      <Spy />
                    </div>
                  </LinkButton>
                </div>

                <ConditionalDisplay displayWhen={[person.password_reset_link]}>
                  <div style={{ margin: '0 10px' }}>
                    <Tooltip position="right" content={'Copied to clipboard'} minWidth>
                      <LinkButton
                        disabled={!person.onboarded}
                        onClick={() => navigator.clipboard.writeText(person.password_reset_link)}
                      >
                        Password Reset Link
                      </LinkButton>
                    </Tooltip>
                  </div>
                </ConditionalDisplay>

                <ConditionalDisplay displayWhen={[person.onboarding_link]}>
                  <Tooltip position="right" content={'Copied to clipboard'} minWidth>
                    <LinkButton
                      onClick={() => navigator.clipboard.writeText(person.onboarding_link)}
                    >
                      Onboarding Link
                    </LinkButton>
                  </Tooltip>
                </ConditionalDisplay>
              </ConditionalDisplay>
            </Actions>
          </ConditionalDisplay>
        </InfoGroup>

        <TeamAndContactInfo>
          <TeamLeadInfoWrapper>
            <ConditionalDisplay displayWhen={[roleType]}>
              <TeamLeadInfoHeader>{roleType}</TeamLeadInfoHeader>
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[team_name]}>
              <TeamLeadInfoHeader>Team {team_name}</TeamLeadInfoHeader>
            </ConditionalDisplay>

            <TeamleadInfoValue>{email}</TeamleadInfoValue>
            <TeamleadInfoValue>{mobile_number}</TeamleadInfoValue>

            <SchoolNameHeader onClick={() => navigate(`/dashboard/organisations?s=${school_name}`)}>
              {school_name}
            </SchoolNameHeader>
          </TeamLeadInfoWrapper>
        </TeamAndContactInfo>
      </Info>
    </Card>
  )
}

export default PersonCard
